/**
 * What the ITEMS stream follows in the rest of the game, by state (so no event can slip past a frozen
 * frame): a player's death and the end of that life, the Baron's arrival, the level clear - and what
 * the C64 does to the items and the carry at each.
 */

import { rngOf } from '../random';
import { placePotions } from './setup';
import { emptySlot } from './slots';
import { cancelStrikes } from './strike';
import { BARON_BASE, BONUS_TIMER_AFTER, CLEAR_BASE_ADD, FOOD_ROLL_MASK } from './tuning';
import type { ItemSystem } from './state';
import type { PlayerId, World } from '../types';

/**
 * A player starts dying: the door flag's bit goes (L_04BB, D_5B7F &= ~player: only starting players
 * had one), the heart's state $18 ends, and D_7F53's red cross ($FF) and lightning (D_37C7 = 0) go.
 * (The C64 does this as the life is taken, after the animation; nothing reads them meanwhile.)
 */
function died(world: World, sys: ItemSystem, id: PlayerId): void {
  world.carry.unharmed[id] = false;
  sys.heartOn[id] = false;
  world.power.redCross[id] = 0;
  world.power.lightning[id] = false;
}

/** D_7F53 as a life ends or a player joins: on level 100 ($7F83) both lightning potions come back. */
function lifeEnded(world: World, sys: ItemSystem): void {
  if (world.level.n >= 100) placePotions(world, sys);
}

function watchPlayers(world: World, sys: ItemSystem): void {
  for (const id of [0, 1] as const) {
    const now = world.players[id].state;
    const was = sys.seenState[id];
    sys.seenState[id] = now;
    if (now === was) continue;
    if (now === 'dying') died(world, sys, id);
    else if (was === 'dying' || now === 'play') lifeEnded(world, sys);
  }
}

/** $1621: the Baron comes - the next food base is marked ($58 = $FF), the yellow cross's strikes stop. */
function watchBaron(world: World, sys: ItemSystem): void {
  const out = world.flow.baron;
  if (out && !sys.seenBaron) {
    world.carry.foodBase = BARON_BASE;
    cancelStrikes(sys);
  }
  sys.seenBaron = out;
}

/**
 * $16F7: the level clears - no more strikes (D_58FF = 0; one on screen finishes), the power-up is
 * cancelled or taken off screen, and the next level's food base is set: after a Baron rand & 3,
 * else the level timer's seconds + 8 ($2A; 50 after the bonus stage, $3117). The food keeps its timer.
 */
function watchClear(world: World, sys: ItemSystem): void {
  if (world.flow.phase !== 'clear' || sys.cleared) return;
  sys.cleared = true;
  sys.strike.passes = 0;
  emptySlot(world, sys.powerup, sys.powerup.visible);
  const c = world.carry;
  if (c.foodBase >= 0 && (c.foodBase & 0x80) !== 0) {
    c.foodBase = rngOf(world).int(256) & FOOD_ROLL_MASK;
    return;
  }
  const seconds = sys.bonusDone ? BONUS_TIMER_AFTER : Math.max(0, world.flow.timer);
  c.foodBase = seconds + CLEAR_BASE_ADD;
}

export function watch(world: World, sys: ItemSystem): void {
  watchPlayers(world, sys);
  watchBaron(world, sys);
  watchClear(world, sys);
}
